import {
  INJURY_AREAS,
  RETURN_DECISION_STATES,
  type InjuryAreaName,
  type ReturnDecisionStateName,
  type ReturnDecisionView,
} from '@steado/admin-contract/user-detail';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';

import { Panel } from '@/components/panel/panels';
import { formatDate, humanise } from '@/lib/format';
import { recordReturnDecision } from './user-detail-query';

const FORM_LABEL = 'text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase';

/** Matches `Input` and `SelectTrigger`, so the row of controls lines up. */
const FORM_SELECT =
  'h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground shadow-xs outline-none focus-visible:border-ring';

interface ReturnDecisionsPanelProps {
  readonly userId: string;
  readonly injuryFlags: readonly string[];
  readonly decisions: readonly ReturnDecisionView[];
}

/**
 * What a user may do with an injured joint again.
 *
 * `injuryFlags` above this panel is what plan generation and the swap route
 * filter on, and before this existed a flag excluded every movement loading
 * that joint forever — a filter that only ever got stricter, with nowhere to
 * record that a physio had cleared someone for lighter work.
 *
 * The list is the whole history, newest first, because "cleared in March,
 * withheld again in May" is the fact an operator needs and the newest row
 * alone cannot tell it. `inForce` comes off the response rather than being
 * re-derived here: which decision applies is a safety rule, and a second
 * implementation of it in a browser is free to drift from the one the plan
 * pipeline reads.
 */
export function ReturnDecisionsPanel({ userId, injuryFlags, decisions }: ReturnDecisionsPanelProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();

  const [injuryArea, setInjuryArea] = useState<InjuryAreaName | ''>('');
  const [decision, setDecision] = useState<ReturnDecisionStateName>('modified');
  const [note, setNote] = useState('');

  const record = useMutation({
    mutationFn: (body: { injuryArea: InjuryAreaName; decision: ReturnDecisionStateName; note?: string }) =>
      recordReturnDecision(api, userId, body),
    // Never retried. The table is append-only, so a silent second attempt
    // records a second decision rather than repeating the first.
    retry: false,
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId, 'detail'] });
    },
  });

  // Only joints the user has actually reported. A decision about a joint that
  // is not flagged changes nothing — the flag is what makes it a constraint —
  // so offering one would be a control with no effect.
  const areas = INJURY_AREAS.filter((area) => injuryFlags.includes(area));

  return (
    <Panel title="Return to load">
      {areas.length === 0 ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          No injuries reported, so there is nothing to clear. A decision only means something for a joint this
          user has flagged.
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {decisions.length === 0 ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Nothing recorded. Until a joint is cleared, every movement loading it is withheld from planning.
              Silence is not a clearance.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {decisions.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-1 border-b border-border pb-3 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="w-[86px] shrink-0 text-xs font-semibold tracking-[0.06em] text-muted-foreground uppercase">
                      {humanise(entry.injuryArea)}
                    </span>
                    <span className="text-sm font-medium text-foreground">{entry.decision}</span>
                    {entry.inForce ? <Badge tone="ok">in force</Badge> : null}
                    <span className="ml-auto shrink-0 font-mono text-[13px] text-muted-foreground">
                      {formatDate(entry.effectiveFrom)}
                    </span>
                  </div>
                  <span className="ml-[98px] text-[13px] text-muted-foreground">
                    {entry.decidedBy}
                    {entry.note === null ? '' : ` · ${entry.note}`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (injuryArea === '') return;
              record.mutate({
                injuryArea,
                decision,
                ...(note.trim() === '' ? {} : { note: note.trim() }),
              });
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor={`return-joint-${userId}`} className={FORM_LABEL}>
                Joint
              </Label>
              {/*
                A native `<select>`, not the console's Radix one: Radix renders no
                `<option>` elements, and the joints offered here are the assertion
                this panel exists for — the list is filtered to what the user has
                actually flagged, and the test reads it as options.
              */}
              <select
                id={`return-joint-${userId}`}
                value={injuryArea}
                onChange={(event) => {
                  setInjuryArea(event.target.value as InjuryAreaName | '');
                }}
                className={FORM_SELECT}
              >
                <option value="">Select…</option>
                {areas.map((area) => (
                  <option key={area} value={area}>
                    {humanise(area)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={`return-decision-${userId}`} className={FORM_LABEL}>
                Decision
              </Label>
              <select
                id={`return-decision-${userId}`}
                value={decision}
                onChange={(event) => {
                  setDecision(event.target.value as ReturnDecisionStateName);
                }}
                className={FORM_SELECT}
              >
                {RETURN_DECISION_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex min-w-[180px] flex-1 flex-col gap-2">
              <Label htmlFor={`return-note-${userId}`} className={FORM_LABEL}>
                Note
              </Label>
              <Input
                id={`return-note-${userId}`}
                type="text"
                value={note}
                maxLength={280}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </div>

            <Button
              type="submit"
              variant="outline"
              disabled={!can.mutate || injuryArea === '' || record.isPending}
              title={can.mutate ? undefined : 'Recording a return decision requires the admin role.'}
            >
              {record.isPending ? 'Recording…' : 'Record'}
            </Button>
          </form>

          <p className="text-[13px] leading-relaxed text-muted-foreground">
            <strong className="font-medium">modified</strong> permits the movement on a constrained load
            prescription: a floor on reps in reserve, stricter again where the movement has not been assessed
            for spinal load or impact. Recorded against your account and audited.
          </p>

          {record.isError ? (
            <p role="alert" className="text-[13px] leading-relaxed text-err-fg">
              {record.error.message}
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
